import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../common/Seo';
import { FaCalendarAlt, FaClock, FaSearch, FaArrowRight } from 'react-icons/fa';
import { BLOG_POSTS, BLOG_CATEGORIES } from '../../data/blog';
import { COMPANY_INFO } from '../../constants';

// Importar imágenes
import cuidadorasImg from '../../assets/cuidadoras.jpg';
import auxiliarImg from '../../assets/auxiliar.jpg';
import generalImg from '../../assets/general.jpg';

const imageMap = {
  cuidadoras: cuidadorasImg,
  auxiliar: auxiliarImg,
  general: generalImg
};

const BlogCard = ({ post }) => {
  const image = imageMap[post.image] || generalImg;
  
  return (
    <article className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group">
      <Link to={`/blog/${post.slug}`}>
        <div className="relative h-48 overflow-hidden">
          <img
            src={image}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          <div className="absolute top-4 left-4">
            <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              {post.category}
            </span>
          </div>
        </div>
      </Link>
      
      <div className="p-6">
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
          <span className="flex items-center gap-1">
            <FaCalendarAlt className="text-blue-500" />
            {new Date(post.date).toLocaleDateString('es-MX', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
          <span className="flex items-center gap-1">
            <FaClock className="text-blue-500" />
            {post.readTime}
          </span>
        </div>
        
        <Link to={`/blog/${post.slug}`}>
          <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
            {post.title}
          </h2>
        </Link>
        
        <p className="text-gray-600 mb-4 line-clamp-3">
          {post.excerpt}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {post.tags.slice(0, 2).map((tag, index) => (
              <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                #{tag}
              </span>
            ))}
          </div>
          
          <Link 
            to={`/blog/${post.slug}`}
            className="text-blue-600 font-medium flex items-center gap-1 hover:gap-2 transition-all"
          >
            Leer más <FaArrowRight className="text-sm" />
          </Link>
        </div>
      </div>
    </article>
  );
};

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('todos');

  const filteredPosts = useMemo(() => {
    return BLOG_POSTS.filter(post => {
      const matchesSearch = searchTerm === '' ||
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = activeCategory === 'todos' || post.category === activeCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, activeCategory]);

  return (
    <>
      <Seo
        title={`Blog de Enfermería y Cuidados Geriátricos | ${COMPANY_INFO.name}`}
        description="Artículos sobre cuidados de adultos mayores, enfermería a domicilio, consejos de salud y guías prácticas para familias."
        canonical="https://geriatriaserena.com/blog"
      />

      <main className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Blog de Geriatría Serena
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Consejos, guías y artículos sobre cuidados geriátricos y enfermería a domicilio
            </p>
          </div>
        </section>

        {/* Search and Filters */}
        <section className="py-8 bg-white shadow-sm sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative w-full md:w-96">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar artículos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Categories */}
              <div className="flex flex-wrap gap-2 justify-center">
                {BLOG_CATEGORIES.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`px-4 py-2 rounded-full font-medium transition-colors ${
                      activeCategory === category.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredPosts.length > 0 ? (
              <>
                <p className="text-gray-600 mb-8">
                  Mostrando {filteredPosts.length} artículo{filteredPosts.length !== 1 ? 's' : ''}
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredPosts.map(post => (
                    <BlogCard key={post.id} post={post} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <FaSearch className="text-6xl text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-600 mb-2">
                  No se encontraron artículos
                </h3>
                <p className="text-gray-500">
                  Intenta con otros términos de búsqueda o categoría
                </p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-blue-600 text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">
              ¿Necesitas ayuda profesional?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Contáctanos para recibir asesoría personalizada sobre el cuidado de tu familiar
            </p>
            <Link
              to="/contact"
              className="inline-block bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors"
            >
              Solicitar Información
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default BlogPage;
